import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Badge from './Badge';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/* 이동 정보 display section - used inside Card across:
   ReservationDetail, TransferComplete, TransferPayment
   from: { label, name?, address, detail?, time?, notes? }
   to: { label, name?, address, detail? }
   stopovers: (string | { name?, address })[]
   arrivalIcon: 'airport' | 'place' etc. */

/* BugHerd #82 follow-up: 명칭(name) 굵게 + 주소(address) 보조 두 줄 표시. name 없으면
   address 단독으로 굵게 (fallback). name 이 이미 address 에 포함되면 중복 제거. */
function PlaceText({ name, address }) {
  if (!address) {
    return <span className="text-sm font-bold text-mono-900">{name || ''}</span>;
  }
  if (name && name !== address && !address.includes(name)) {
    return (
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-bold text-mono-900">{name}</span>
        <span className="text-2sm font-medium text-mono-500">{address}</span>
      </span>
    );
  }
  return <span className="text-sm font-bold text-mono-900">{address}</span>;
}

export default function RouteInfoSection({
  directionLabel,
  from,
  to,
  via,
  stopovers = [],
  arrivalIcon = 'airport',
}) {
  const { t } = useTranslation();

  // R: directionLabel + from/to/via/stopover의 name/address/label/notes/detail 전부 DeepL 번역
  const bundle = useMemo(() => {
    const out = [directionLabel || ''];
    const safe = (v) => (typeof v === 'string' ? v : '');
    out.push(safe(from?.label), safe(from?.name), safe(from?.address), safe(from?.detail), safe(from?.time), safe(from?.notes));
    out.push(safe(to?.label), safe(to?.name), safe(to?.address), safe(to?.detail));
    out.push(safe(via?.label), safe(via?.name), safe(via?.address));
    stopovers.forEach((sp) => {
      const n = typeof sp === 'string' ? '' : safe(sp?.name);
      const a = typeof sp === 'string' ? sp : safe(sp?.address);
      out.push(n, a);
    });
    return out;
  }, [directionLabel, from, to, via, stopovers]);
  const tBundle = useDeepLTranslated(bundle);

  let idx = 0;
  const tDir = tBundle[idx++] || directionLabel;
  const tFrom = {
    ...from,
    label: tBundle[idx++] || from?.label,
    name: tBundle[idx++] || from?.name,
    address: tBundle[idx++] || from?.address,
    detail: tBundle[idx++] || from?.detail,
    time: tBundle[idx++] || from?.time,
    notes: tBundle[idx++] || from?.notes,
  };
  const tTo = {
    ...to,
    label: tBundle[idx++] || to?.label,
    name: tBundle[idx++] || to?.name,
    address: tBundle[idx++] || to?.address,
    detail: tBundle[idx++] || to?.detail,
  };
  const tVia = via ? {
    ...via,
    label: tBundle[idx++] || via?.label,
    name: tBundle[idx++] || via?.name,
    address: tBundle[idx++] || via?.address,
  } : null;
  if (!via) idx += 3;
  const tStopovers = stopovers.map((sp) => {
    const n = tBundle[idx++];
    const a = tBundle[idx++];
    if (typeof sp === 'string') return a || sp;
    return { ...sp, name: n || sp?.name, address: a || sp?.address };
  });

  return (
    <>
      <h3 className="text-lg font-bold text-mono-900">{t('common.route_info_title')}</h3>
      <Badge variant="direction">{tDir}</Badge>

      <div className="flex flex-col gap-2.5">
        {/* Departure */}
        <div className="flex items-start gap-3 sm:gap-4">
          <Icon name={from.icon || 'van'} size={28} className="text-mono-600 shrink-0 mt-2" />
          <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-3 flex flex-col gap-1">
            <span className="text-2sm font-medium text-mono-500">{tFrom.label}</span>
            <PlaceText name={tFrom.name} address={tFrom.address} />
            {tFrom.time && <span className="text-2sm font-medium text-mono-600">{tFrom.time}</span>}
            {tFrom.detail && (
              <input
                type="text"
                readOnly
                value={tFrom.detail}
                className="mt-1 w-full px-3 py-2 bg-white border border-mono-200 rounded-lg text-sm font-medium text-mono-700 cursor-default focus:outline-none"
              />
            )}
          </div>
        </div>

        {/* Via (골프장/스키장 경유) */}
        {tVia && (
          <>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-7 flex items-center justify-center shrink-0">
                <div className="h-8 border-l-2 border-dashed border-text-muted" />
              </div>
            </div>
            <div className="flex items-start gap-3 sm:gap-4">
              <Icon name={tVia.icon || 'place'} size={28} className="text-mono-600 shrink-0 mt-2" />
              <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-3 flex flex-col gap-1">
                <span className="text-2sm font-medium text-mono-500">{tVia.label}</span>
                <PlaceText name={tVia.name} address={tVia.address} />
              </div>
            </div>
          </>
        )}

        {/* Free stopovers (경유지) — 문자열 or {name, address} 객체 허용 */}
        {tStopovers.length > 0 && tStopovers.map((sp, i) => {
          const stopName = typeof sp === 'string' ? '' : (sp?.name || '');
          const stopAddr = typeof sp === 'string' ? sp : (sp?.address || '');
          return (
            <React.Fragment key={i}>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-7 flex items-center justify-center shrink-0">
                  <div className="h-8 border-l-2 border-dashed border-text-muted" />
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <Icon name="place" size={28} className="text-mono-600 shrink-0 mt-2" />
                <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-3 flex flex-col gap-1">
                  <span className="text-2sm font-medium text-mono-500">{t('common.stopover')} {stopovers.length > 1 ? i + 1 : ''}</span>
                  <PlaceText name={stopName} address={stopAddr} />
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {/* Dashed connector */}
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-7 flex items-center justify-center shrink-0">
            <div className="h-8 border-l-2 border-dashed border-text-muted" />
          </div>
        </div>

        {/* Arrival */}
        <div className="flex items-start gap-3 sm:gap-4">
          <Icon name={arrivalIcon} size={28} className="text-mono-600 shrink-0 mt-2" />
          <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-3 flex flex-col gap-1">
            <span className="text-2sm font-medium text-mono-500">{tTo.label}</span>
            <PlaceText name={tTo.name} address={tTo.address} />
            {tTo.detail && (
              <input
                type="text"
                readOnly
                value={tTo.detail}
                className="mt-1 w-full px-3 py-2 bg-white border border-mono-200 rounded-lg text-sm font-medium text-mono-700 cursor-default focus:outline-none"
              />
            )}
          </div>
        </div>

        {/* 요청사항 — 이동 정보 하단에 별도 표시 */}
        {tFrom.notes && (
          <div className="flex items-start gap-3 sm:gap-4 mt-1">
            <div className="w-7 shrink-0" />
            <div className="flex-1 bg-mono-50 rounded-lg px-3 sm:px-5 py-3 flex flex-col gap-1">
              <span className="text-2sm font-medium text-mono-500">{t('common.request_label', '요청사항')}</span>
              <span className="text-2sm font-normal text-mono-700 whitespace-pre-line">{tFrom.notes}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
